/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { APIEndpoint } from '../../../utils/axios';
import { DataGrid, EditModal, UploadModal, DeleteConf } from '../../../common';
import { onDownloadXlsFile } from '../../../utils/commonFunctions';
import usePermissions from '../../../utils/userPermissions';

// Icons
import searchIcon from '../../../assets/icons/search.svg';
import editIcon2 from '../../../assets/edit2.svg';
import deleteIcon from '../../../assets/delete.svg';
import uploadItemIcon from '../../../assets/icons/upload-items.svg';


const escapeHtml = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const getValidIdentifier = (row) => {
  if (!row) return null;
  const id = row.id || row._id || row.iata;
  return (id && id !== 'N/A') ? id : null;
};

// Constants & Form Field Definitions
const BASE_FORM_FIELDS = [
  { label: 'IATA Code', attribute: 'iata', type: 'text', disabled: true },
  { label: 'ICAO Code', attribute: 'icao', type: 'text' },
  { label: 'Airport Name', attribute: 'airport', type: 'text', required: true },
  { label: 'Country', attribute: 'country_code', type: 'text', required: true },
  { label: 'Time Zone', attribute: 'time', type: 'text' },
  { label: 'UTC Offset', attribute: 'utc', type: 'text' },
  { label: 'Airport Type', attribute: 'type', type: 'text' },
  { label: 'Scheduled Service', attribute: 'scheduled_service', type: 'text' },
  { label: 'Source', attribute: 'source', type: 'text' },
];

const ADD_FORM_FIELDS_TEMPLATE = [
  { label: 'IATA Code', attribute: 'iata', type: 'text', required: true },
  { label: 'ICAO Code', attribute: 'icao', type: 'text' },
  { label: 'Airport Name', attribute: 'airport', type: 'text', required: true },
  { label: 'Country', attribute: 'country_code', type: 'text', required: true },
  { label: 'Time Zone', attribute: 'time', type: 'text' },
  { label: 'UTC Offset', attribute: 'utc', type: 'text' },
  { label: 'Airport Type', attribute: 'type', type: 'text' },
  { label: 'Scheduled Service', attribute: 'scheduled_service', type: 'text' },
  { label: 'Source', attribute: 'source', type: 'text' },
];

// Data Normalization
const normalizeAirport = (airport = {}) => {
  const getVal = (...keys) => {
    for (let k of keys) {
      if (airport[k] !== undefined && airport[k] !== null) {
        const str = String(airport[k]).trim();
        if (str !== '') return str;
      }
    }
    return '';
  };

  let tz = getVal('time', 'time_zone', 'timezone');
  if (!tz && airport.timezone && typeof airport.timezone === 'object') {
    tz = airport.timezone.name || airport.timezone.tz || '';
  }

  let rawUtc = airport.utc !== undefined ? airport.utc : (airport.utc_offset !== undefined ? airport.utc_offset : (airport.utcOffset !== undefined ? airport.utcOffset : undefined));
  let utcVal = '';
  if (rawUtc !== undefined && rawUtc !== null && String(rawUtc).trim() !== '') {
    utcVal = String(rawUtc).trim();
  } else if (airport.timezone && typeof airport.timezone === 'object') {
    utcVal = String(airport.timezone.utcOffsetText || airport.timezone.utcOffset || airport.timezone.offset || '').trim();
  }

  const iataVal = getVal('iata', 'iata_code', 'iataCode', 'locationCode');
  const icaoVal = getVal('icao', 'icao_code', 'icaoCode');
  const airportVal = getVal('airport', 'airport_name', 'airportName', 'locationName');
  const countryVal = getVal('country_code', 'countryCode', 'country');
  const typeVal = getVal('type', 'airport_type', 'airportType');
  const serviceVal = getVal('scheduled_service', 'scheduledService');
  const sourceVal = getVal('source');

  return {
    ...airport,
    iata: iataVal || 'N/A',
    icao: icaoVal || 'N/A',
    airport: airportVal || 'N/A',
    country_code: countryVal || 'N/A',
    time: tz || 'N/A',
    utc: utcVal !== '' ? utcVal : 'N/A',
    type: typeVal || 'N/A',
    scheduled_service: serviceVal || 'N/A',
    source: sourceVal || 'N/A',
  };
};

export const GlobalAirportsPage = () => {
  const { t, ready } = useTranslation();
  const tableRef = useRef();

  // Permission Hooks
  const { hasPermission: editAirportsMaster } = usePermissions('edit', 'orn:Account:ModifyAirportMaster');
  const { hasPermission: editAirportsGlobal } = usePermissions('edit', 'configuration.fsc.global-airports');
  const editAirports = editAirportsMaster || editAirportsGlobal;

  const { hasPermission: createAirportsMaster } = usePermissions('create', 'orn:Account:ModifyAirportMaster');
  const { hasPermission: createAirportsGlobal } = usePermissions('create', 'configuration.fsc.global-airports');
  const createAirports = createAirportsMaster || createAirportsGlobal;

  const { hasPermission: uploadAirportsMaster } = usePermissions('upload', 'orn:Account:ModifyAirportMaster');
  const { hasPermission: uploadAirportsGlobal } = usePermissions('upload', 'configuration.fsc.global-airports');
  const uploadAirports = uploadAirportsMaster || uploadAirportsGlobal;

  const { hasPermission: deleteAirportsMaster } = usePermissions('delete', 'orn:Account:ModifyAirportMaster');
  const { hasPermission: deleteAirportsGlobal } = usePermissions('delete', 'configuration.fsc.global-airports');
  const deleteAirports = deleteAirportsMaster || deleteAirportsGlobal;

  // State
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchKey, setSearchKey] = useState('');

  // Form & Modal State
  const [showUpload, setShowUpload] = useState(false);
  const [modalStatus, setModalStatus] = useState({ text: '', error: '' });
  const [uploadedFile, setUploadedFile] = useState([]);
  const [uploadStatusBlock, setUploadStatusBlock] = useState('');
  const [addFormFields] = useState(ADD_FORM_FIELDS_TEMPLATE);

  // Column Definitions
  const columns = useMemo(
    () => [
      { field: 'iata', headerName: 'IATA Code', flex: 1, minWidth: 110 },
      { field: 'icao', headerName: 'ICAO Code', flex: 1, minWidth: 110 },
      { field: 'airport', headerName: 'Airport Name', flex: 1.5, minWidth: 200, cellStyle: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } },
      { field: 'country_code', headerName: 'Country', flex: 1, minWidth: 100 },
      { field: 'time', headerName: 'Time Zone', flex: 1.2, minWidth: 160 },
      { field: 'utc', headerName: 'UTC Offset', flex: 1, minWidth: 110 },
      { field: 'source', headerName: 'Source', flex: 1, minWidth: 130 },
      { field: 'type', headerName: 'Airport Type', flex: 1, minWidth: 140 },
      { field: 'scheduled_service', headerName: 'Scheduled Service', flex: 1, minWidth: 150 },
    ],
    []
  );

  // Grid Action Definitions based on Permissions
  const actions = useMemo(() => {
    const detail = [];
    if (editAirports) {
      detail.push({ src: editIcon2, value: 'EditItem', title: 'Edit' });
    }
    if (deleteAirports) {
      detail.push({ src: deleteIcon, value: 'DeleteItem', title: 'Delete' });
    }
    return detail.length > 0 ? [{ title: '', detail }] : [];
  }, [editAirports, deleteAirports]);

  // GRID DATA FETCHING
  const fetchGlobalAirports = useCallback(
    async (page = 1, perPage = 50) => {
      const response = await APIEndpoint.post('global-airports/process', {
        action: 4,
        collection: '',
        filterOperation: 'or',
        filters: [],
        limit: perPage,
        page,
        searchParam: searchKey,
      });

      const rawData = response?.items || response?.data?.items || response?.data || [];
      const dataset = rawData.map(normalizeAirport);
      return { items: dataset, count: response?.count || dataset.length };
    },
    [searchKey]
  );

  // FORM HANDLERS
  const removeContainerOverlay = () => {
    (document.querySelectorAll('.content-container')[0])?.classList?.remove('modal-overlay-bg2');
  };

  const handleClose = () => {
    setSelectedRow(null);
    setModalStatus({ text: '', error: '' });
    removeContainerOverlay();
  };

  const handleResetAndRefresh = () => {
    setTimeout(() => {
      tableRef.current?.refreshTableHandler();
      handleClose();
    }, 500);
  };

  const handleEditSubmit = async (e, updated, editHsn, mode) => {
    const cleanUpdated = updated && typeof updated === 'object'
      ? Object.fromEntries(Object.entries(updated).filter(([_, val]) => val !== undefined))
      : {};

    const isEditMode = selectedRow?.action === 'EditItem';
    const isCreateMode = selectedRow?.action === 'AddNewItem';

    if (isEditMode && editAirports) {
      const id = getValidIdentifier(selectedRow);
      if (!id) {
        setModalStatus({ error: 'Failed to update airport data. Invalid identifier.' });
        return;
      }
      const fields = ['iata', 'icao', 'airport', 'country_code', 'time', 'utc', 'type', 'scheduled_service', 'source'];
      const payload = {};
      fields.forEach((key) => {
        const val = cleanUpdated[key] !== undefined ? cleanUpdated[key] : selectedRow[key];
        payload[key] = (val === 'N/A' || val === undefined || val === null) ? '' : val;
      });

      try {
        const res = await APIEndpoint.put(`global-airports/${id}`, payload);
        if (res?.success) {
          setModalStatus({ text: res.msg || 'Data Saved Successfully' });
          handleResetAndRefresh();
        }
      } catch (error) {
        try {
          const errMsg = JSON.parse(error.message);
          setModalStatus({ error: errMsg.data?.message || errMsg.data?.msg || 'Failed to update airport data.' });
        } catch {
          setModalStatus({ error: 'Failed to update airport data.' });
        }
      }
    } else if (isCreateMode && createAirports) {
      if (Object.keys(cleanUpdated).length > 0) {
        const payload = {
          ...cleanUpdated,
        };

        const missingFields = [
          ['iata', 'IATA Code'],
          ['airport', 'Airport Name'],
          ['country_code', 'Country'],
        ].filter(([field]) => !String(payload[field] || '').trim());

        if (missingFields.length > 0) {
          setModalStatus({ error: `Please enter: ${missingFields.map(([, label]) => label).join(', ')}.` });
          return;
        }

        try {
          const res = await APIEndpoint.post('global-airports', payload);
          if (res?.success) {
            setModalStatus({ text: res.msg || 'Data Saved Successfully' });
            handleResetAndRefresh();
          }
        } catch (error) {
          try {
            const errMsg = JSON.parse(error.message);
            setModalStatus({ error: errMsg.data?.message || errMsg.data?.msg || 'Something went wrong.' });
          } catch {
            setModalStatus({ error: 'Failed to create airport.' });
          }
        }
      } else {
        setModalStatus({ error: 'Please fill all required fields!' });
      }
    }
  };

  // Soft delete airport
  const handleDeleteSubmit = async () => {
    if (!deleteAirports) return;
    const id = getValidIdentifier(selectedRow);
    if (!id) {
      setModalStatus({ error: 'Failed to delete airport. Invalid identifier.' });
      return;
    }
    try {
      const res = await APIEndpoint.delete(`global-airports/${id}`);
      if (res?.success) {
        setModalStatus({ text: res.msg || 'Airport deleted successfully' });
        // Refresh table after delete
        handleResetAndRefresh();
      } else {
        setModalStatus({ error: res?.msg || 'Failed to delete airport.' });
      }
    } catch (error) {
      setModalStatus({ error: 'Failed to delete airport.' });
    }
  };

  // UPLOAD HANDLERS
  const onDownloadSampleFile = () => {
    onDownloadXlsFile('samples/downloads/Global_Airports_Upload_Sample', 'Global_Airports_Upload_Sample');
  };

  const onUploadFileChangeHandler = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    setUploadStatusBlock('<p>Please wait while we process your data...</p>');

    const formData = new FormData();
    formData.append('scheduleLater', false);
    formData.append('file', file);

    try {
      await APIEndpoint.post('global-airports/upload', formData);
      setUploadedFile([]);
      setUploadStatusBlock('<p>File Imported Successfully.</p>');
      tableRef.current?.refreshTableHandler();
    } catch (error) {
      e.target.value = null;
      setUploadedFile([]);
      let msgHeading = 'Error uploading airports sheet';
      let msgContent = 'Error occurred while uploading airports! Please check the file and try again.';

      try {
        if (error && typeof error.message === 'string') {
          const parsed = JSON.parse(error.message);
          const status = parsed?.status;
          const data = parsed?.data;

          if (data) {
            msgHeading = escapeHtml(data.Error || data.message || msgHeading);
          }

          if (status === 400 && data && Array.isArray(data.errors)) {
            let errorLines = [];
            data.errors.forEach((err) => {
              if (err && err.lineNumber !== undefined) {
                const lineNum = escapeHtml(err.lineNumber);
                let fieldErrors = '';
                if (Array.isArray(err.errors)) {
                  fieldErrors = err.errors
                    .map((eObj) => {
                      if (eObj && typeof eObj === 'object') {
                        const fieldStr = eObj.field ? escapeHtml(eObj.field) + ' | ' : '';
                        const msgStr = eObj.message ? escapeHtml(eObj.message) : '';
                        return fieldStr + msgStr;
                      }
                      return '';
                    })
                    .filter(Boolean)
                    .join('<br>');
                }
                if (fieldErrors) {
                  errorLines.push(`Line ${lineNum}:<br>${fieldErrors}`);
                }
              }
            });
            if (errorLines.length > 0) {
              msgContent = errorLines.join('<br><br>');
            }
          }
        }
      } catch (errParse) {
        msgContent = 'An unexpected error occurred during upload.';
      }

      setUploadStatusBlock(
        `<p class='listErrorMessagesCls'>${msgHeading}</p><p class='listErrorMessagesCls sm-scrollbar border p-2 pt-1'>${msgContent}</p>`
      );
    }
  };

  return (
    <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark">
      {/* HEADER CONTROLS */}
      <div className="flexRow width100 justifyContentSpaceBetween">
        <div className="width100Menu justifyContentCenter flexRow">
          <div className="margLeft underline">
            <span><img className="icon-size" alt="search" src={searchIcon} /></span>
            <span>
              <input
                className="search"
                onChange={(e) => setSearchKey(e.target.value)}
                type="search"
                placeholder="Search"
                value={searchKey}
              />
            </span>
          </div>
        </div>

        {uploadAirports && (
          <div className="flexRow justifyContentFlexEnd margBot10">
            <span
              title={t('pages.pagesContent.airports.uploadButtonTitle', { defaultValue: 'Upload Airports' })}
              className="upload dropdownStyle1-TextMedium"
              onClick={() => setShowUpload(true)}
            >
              <img className="icon-size-min" alt="uploadItem" src={uploadItemIcon} />
            </span>
          </div>
        )}

        {createAirports && (
          <div className="margLeft flexRow justifyContentFlexEnd alignItemsBaseline">
            <button
              className="add-item-button"
              onClick={() => setSelectedRow({ action: 'AddNewItem' })}
            >
              {t('pages.pagesContent.airports.addModalTitleButton', { defaultValue: '+ Airport' })}
            </button>
          </div>
        )}
      </div>

      {/* DATA GRID */}
      <DataGrid
        ref={tableRef}
        className="full-flex"
        columns={columns}
        initialPageLimit={50}
        showDefaultFilters={false}
        searchKey={searchKey}
        actions={actions}
        onAction={(e, action, row) => {
          if (action?.value === 'DeleteItem' && deleteAirports) {
            setSelectedRow({ ...row, action: 'DeleteItem' });
          } else if (action?.value === 'EditItem' && editAirports) {
            setSelectedRow({ ...row, action: 'EditItem' });
          }
        }}
        onAddItemHandler={createAirports ? () => setSelectedRow({ action: 'AddNewItem' }) : undefined}
        getRows={fetchGlobalAirports}
        noResultsMessage="No data found"
      />

      {/* EDIT / CREATE MODALS */}
      {selectedRow && selectedRow.action === 'EditItem' && editAirports && (
        <EditModal
          title={t('pages.pagesContent.airports.editModalTitle', { defaultValue: 'Edit Airport' })}
          onClose={handleClose}
          buttonLabel="Save"
          modalFormStatusObj={modalStatus}
          showFieldsInColumn={true}
          customContentHeight={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', paddingRight: '1rem' }}
          onSubmit={handleEditSubmit}
          onChange={() => {
            setModalStatus({ text: '', error: '' });
          }}
          fields={BASE_FORM_FIELDS.map((field) => ({
            ...field,
            initialValue: selectedRow[field.attribute],
          }))}
        />
      )}

      {selectedRow && selectedRow.action === 'AddNewItem' && createAirports && (
        <EditModal
          title={t('pages.pagesContent.airports.addModalTitle', { defaultValue: 'Create Airport' })}
          modalFormStatusObj={modalStatus}
          onClose={handleClose}
          buttonLabel="Save"
          showFieldsInColumn={true}
          customContentHeight={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', paddingRight: '1rem' }}
          onSubmit={handleEditSubmit}
          onChange={() => {
            setModalStatus({ text: '', error: '' });
          }}
          fields={addFormFields.map((field) => ({
            ...field,
            initialValue: field.value ?? '',
          }))}
        />
      )}

      {selectedRow && selectedRow.action === 'DeleteItem' && deleteAirports && (
        <DeleteConf
          description={`Are you sure you want to delete airport '${(selectedRow.iata && selectedRow.iata !== 'N/A') ? selectedRow.iata : (selectedRow.airport && selectedRow.airport !== 'N/A' ? selectedRow.airport : 'Unknown')}'?`}
          onCloseDelete={handleClose}
          onContinue={handleDeleteSubmit}
          modalFormStatusObj={modalStatus}
        />
      )}

      {/* UPLOAD MODAL */}
      {showUpload && (
        <UploadModal
          modelTitle={t('pages.pagesContent.airports.uploadButtonTitle', { defaultValue: 'Upload Airports' })}
          onUploadClose={() => {
            setUploadedFile([]);
            setUploadStatusBlock('');
            setShowUpload(false);
          }}
          onDownloadSampleFile={onDownloadSampleFile}
          onUploadFileChangeHandler={onUploadFileChangeHandler}
          uploadedFile={uploadedFile}
          uploadFileStatusBlock={uploadStatusBlock}
        />
      )}

      {!ready && <div className="overlay_hide sub" />}
    </div>
  );
};

export default GlobalAirportsPage;


